import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Save, Bot, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { StepMetadata } from "@/components/wizard/StepMetadata";
import { StepDistribution } from "@/components/wizard/StepDistribution";
import { StepTracks, newTrack } from "@/components/wizard/StepTracks";
import { StepDispatch } from "@/components/wizard/StepDispatch";
import { artistDisplay } from "@/components/wizard/ArtistList";
import { cn } from "@/lib/utils";
import { t } from "@/i18n";

const STEPS = (staff) => ["Metadata & portada", "Territorios & tiendas", "Tracks", staff ? "Revisión & envío" : "Revisión"];

const empty = () => ({
  title: "", artist_name: "", primary_artists: [""], featured_artists: [], release_type: "Single", label: "Doble R Records", primary_genre: "", secondary_genre: "", language: "es",
  live_date: "", original_release_date: "", upc: "", cover_file_id: null, cover_filename: null, contract_file_id: null, contract_filename: null,
  c_line: "", p_line: "", copyright_year: new Date().getFullYear(), territories: { mode: "worldwide", countries: [] }, stores: [], notes: "", tracks: [],
});

const names = (list) => (list || []).map((n) => n.trim()).filter(Boolean);

const load = (r) => {
  const primary = names(r.primary_artists);
  return { ...empty(), ...r, primary_artists: primary.length ? primary : [r.artist_name || ""], featured_artists: names(r.featured_artists) };
};

const clean = (rel) => ({
  ...rel,
  primary_artists: names(rel.primary_artists), featured_artists: names(rel.featured_artists),
  artist_name: artistDisplay(rel.primary_artists, rel.featured_artists),
  upc: rel.upc || null, live_date: rel.live_date || null, original_release_date: rel.original_release_date || null,
  primary_genre: rel.primary_genre || null, secondary_genre: rel.secondary_genre || null,
  tracks: rel.tracks.map((t) => ({ ...t, isrc: t.isrc || null, version: t.version || null, lyrics: t.lyrics || null, publisher: t.publisher || null,
    artists: t.artists.filter((p) => p.name.trim()), contributors: t.contributors.filter((p) => p.name.trim()) })),
});

export default function ReleaseWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [rel, setRel] = useState(empty);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);
  const set = (patch) => setRel((r) => ({ ...r, ...patch }));

  useEffect(() => {
    if (id) api.get(`/releases/${id}`).then((r) => setRel(load(r.data))).catch((e) => { toast.error(errMsg(e)); navigate("/lanzamientos"); });
    if (can("admin", "manager")) api.get("/settings/downtown").then((r) => setSettings(r.data)).catch(() => {});
  }, [id]); // eslint-disable-line

  const validate = (s) => {
    if (s === 0 && (!rel.title.trim() || names(rel.primary_artists).length === 0)) return t("Título y artista son obligatorios");
    if (s === 0) {
      const p = names(rel.primary_artists).map((n) => n.toLowerCase());
      const dup = names(rel.featured_artists).find((n) => p.includes(n.toLowerCase()));
      if (dup) return t("{name} ya es artista principal; no puede ser también invitado", { name: dup });
    }
    if (s === 2 && (rel.tracks.length === 0 || rel.tracks.some((t) => !t.title.trim()))) return t("Agrega al menos un track con título");
    if (s === 2) {
      const bad = rel.tracks.flatMap((t) => t.contributors.filter((c) => ["Composer", "Lyricist", "Songwriter"].includes(c.role) && c.name.trim().split(/\s+/).length < 2).map((c) => c.name.trim())).filter(Boolean);
      if (bad.length) return t("Compositores y letristas deben llevar nombre y apellido (el distribuidor lo exige): {names}", { names: [...new Set(bad)].join(", ") });
    }
    return null;
  };

  const next = () => {
    const err = validate(step);
    if (err) return toast.error(err);
    if (step === 1 && rel.tracks.length === 0) set({ tracks: [newTrack(rel)] });
    setStep((s) => Math.min(s + 1, 3));
  };

  const [issues, setIssues] = useState(null);
  const staff = can("admin", "manager");

  const save = async (dispatch = false) => {
    for (const s of [0, 2]) { const err = validate(s); if (err) { setStep(s); return toast.error(err); } }
    setBusy(true);
    try {
      const body = clean(rel);
      const { data } = id ? await api.put(`/releases/${id}`, body) : await api.post("/releases", body);
      if (!id) navigate(`/lanzamientos/${data.id}/editar`, { replace: true });
      if (dispatch) {
        try {
          const sub = await api.post(`/releases/${data.id}/submit`);
          toast.success(t("Lanzamiento enviado a distribución"));
          return navigate(staff ? `/bot/${sub.data.job_id}` : `/lanzamientos/${data.id}`);
        } catch (e) {
          const d = e.response?.data?.detail;
          if (e.response?.status === 422 && d?.issues) { setIssues(d.issues); setStep(3); return toast.error(t("Revisa estos puntos antes de enviar")); }
          throw e;
        }
      }
      toast.success(t(id ? "Lanzamiento actualizado" : "Lanzamiento creado"));
      navigate(`/lanzamientos/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t(id ? "Editar lanzamiento" : "Nuevo lanzamiento")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{rel.title || t("Sin título")}</h1>
        </div>
        <Button variant="ghost" onClick={() => navigate(-1)} data-testid="btn-wizard-cancel">{t("Cancelar")}</Button>
      </div>
      <ol className="grid grid-cols-4 gap-2" data-testid="wizard-steps">
        {STEPS(can("admin", "manager")).map((label, i) => (
          <li key={label}>
            <button type="button" onClick={() => i < step && setStep(i)} data-testid={`wizard-step-${i}`} data-active={i === step}
              className={cn("w-full text-left rounded-md border p-3 transition-colors", i === step ? "border-primary/60 bg-primary/10" : i < step ? "border-border bg-card cursor-pointer hover:border-primary/40" : "border-border/60 opacity-60")}>
              <p className="font-mono text-[11px] text-muted-foreground">{t("PASO {n}", { n: i + 1 })}</p>
              <p className={cn("text-sm font-medium truncate", i === step && "text-primary")}>{t(label)}</p>
            </button>
          </li>
        ))}
      </ol>
      <div className="panel p-6 sm:p-8 rise" key={step}>
        {step === 0 && <StepMetadata rel={rel} set={set} />}
        {step === 1 && <StepDistribution rel={rel} set={set} />}
        {step === 2 && <StepTracks rel={rel} set={set} />}
        {step === 3 && <StepDispatch rel={rel} settings={settings} issues={issues} />}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="btn-wizard-prev-step" className="gap-2"><ArrowLeft className="h-4 w-4" /> {t("Anterior")}</Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => save(false)} disabled={busy} data-testid="btn-wizard-save" className="gap-2"><Save className="h-4 w-4" /> {t("Guardar")}</Button>
          {step < 3 ? (
            <Button onClick={next} data-testid="btn-wizard-next-step" className="gap-2">{t("Siguiente")} <ArrowRight className="h-4 w-4" /></Button>
          ) : (
            <Button onClick={() => save(true)} disabled={busy} data-testid="btn-wizard-save-dispatch" className="gap-2">{staff ? <Bot className="h-4 w-4" /> : <Send className="h-4 w-4" />} {t(staff ? "Guardar y enviar a Downtown" : "Guardar y enviar a distribución")}</Button>
          )}
        </div>
      </div>
    </div>
  );
}
